import React, { createContext, useContext, useState, useCallback } from 'react';
import { useLingui } from '@lingui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { trackManagerAction } from '@services/amplitude.service';
import usersApi from '../api/users.api';

// --- Step definitions ----------------------------------------------------------

const buildSteps = i18n => [
  // -- menus-page --------------------------------------------------------------
  {
    id: 'menus-welcome',
    screen: 'menus',
    targetId: null,
    title: i18n._('Welcome to your digital menu! 👋'),
    body: i18n._(
      'In a few steps we will guide you to create your first menu card. A "card" is a themed menu — for example a Wine List, Pizzas or Cocktails.',
    ),
    action: 'tap',
    position: 'center',
  },
  {
    id: 'menus-add-button',
    screen: 'menus',
    targetId: 'tutorial-add-menu-btn',
    title: i18n._('Create your first menu card'),
    body: i18n._(
      'Tap this button to add your first card. You can call it "Pizzas", "Cocktails", "Wine List" — whatever you like.',
    ),
    action: 'tap',
    position: 'above',
  },
  // -- edit-menu-page -----------------------------------------------------------
  {
    id: 'edit-menu-name',
    screen: 'edit-menu',
    targetId: 'tutorial-menu-name-field',
    title: i18n._('Give your card a name'),
    body: i18n._(
      'Type the name your customers will see. Example: "Pizzas", "Cocktails", "Wine List". Then tap "Continue".',
    ),
    action: 'tap',
    position: 'below',
    interactive: true,
  },
  {
    id: 'edit-menu-save',
    screen: 'edit-menu',
    targetId: 'tutorial-save-menu-btn',
    title: i18n._('Save the card'),
    body: i18n._('Done writing the name? Tap here to save and continue.'),
    action: 'tap',
    position: 'above',
  },
  // -- menus-page (after save) --------------------------------------------------
  {
    id: 'menus-open-card',
    screen: 'menus',
    targetId: 'tutorial-menu-card-0',
    title: i18n._('Great! Your card has been created ✅'),
    body: i18n._(
      'Now tap the card to open it and start adding categories - like "Starters", "Reds", "Classics".',
    ),
    action: 'tap',
    position: 'below',
  },
  // -- categories-page ----------------------------------------------------------
  {
    id: 'categories-welcome',
    screen: 'categories',
    targetId: null,
    title: i18n._('Manage your categories here 📁'),
    body: i18n._(
      'Categories group your products. For example in "Pizzas" you could have "Tomato", "White" and "Fried". Start by adding the first one.',
    ),
    action: 'tap',
    position: 'center',
  },
  {
    id: 'categories-add-button',
    screen: 'categories',
    targetId: 'tutorial-add-category-btn',
    title: i18n._('Add the first category'),
    body: i18n._('Tap here, type the category name in the window that appears and confirm.'),
    action: 'tap',
    position: 'above',
  },
  // -- categories-page (after adding category) ----------------------------------
  {
    id: 'categories-open-category',
    screen: 'categories',
    targetId: 'tutorial-category-card-0',
    title: i18n._('Perfect! Category created ✅'),
    body: i18n._('Now tap the category to open it and add your products.'),
    action: 'tap',
    position: 'below',
  },
  // -- items-page ---------------------------------------------------------------
  {
    id: 'items-welcome',
    screen: 'items',
    targetId: null,
    title: i18n._('Add your products here 🍕'),
    body: i18n._(
      'This screen shows all the dishes or drinks in this category. Add your first product by tapping the button below.',
    ),
    action: 'tap',
    position: 'center',
  },
  {
    id: 'items-add-button',
    screen: 'items',
    targetId: 'tutorial-add-item-btn',
    title: i18n._('Add the first product'),
    body: i18n._('Tap here to add a dish or drink with name, price and description.'),
    action: 'tap',
    position: 'above',
  },
  // -- edit-item-page -----------------------------------------------------------
  {
    id: 'edit-item-name',
    screen: 'edit-item',
    targetId: 'tutorial-item-name-field',
    title: i18n._('Product name'),
    body: i18n._(
      'Type the product name - for example "Margherita", "Negroni" or "Craft beer". Then tap "Continue".',
    ),
    action: 'tap',
    position: 'below',
    interactive: true,
  },
  {
    id: 'edit-item-price',
    screen: 'edit-item',
    targetId: 'tutorial-item-price-field',
    title: i18n._('Price'),
    body: i18n._(
      'Enter the price in euros (e.g. 8.50). You can always change it later. Then tap "Continue".',
    ),
    action: 'tap',
    position: 'above',
    interactive: true,
  },
  {
    id: 'edit-item-save',
    screen: 'edit-item',
    targetId: 'tutorial-save-item-btn',
    title: i18n._('Save the product'),
    body: i18n._('When you are done, tap here to save. You can add more products straight after.'),
    action: 'tap',
    position: 'above',
  },
  // -- final step (branding upload) --------------------------------------------
  {
    id: 'branding-upload',
    screen: 'items',
    targetId: null,
    title: i18n._('Almost done! Want a custom design? 🎨'),
    body: i18n._(
      'Upload your venue logo and a background image. Our AI will create a personalised menu design and send it to you by whatsapp!',
    ),
    action: 'tap',
    position: 'center',
  },
];

export const TOTAL_STEPS = 14;

// --- Context -------------------------------------------------------------------

const TutorialContext = createContext(null);

export function TutorialProvider({ children }) {
  const { i18n } = useLingui();
  const queryClient = useQueryClient();
  const completeTutorialMutation = useMutation(() => usersApi.completeTutorial(), {
    onSuccess: async () => {
      await queryClient.invalidateQueries(['getUser']);
    },
  });

  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [showBrandingUpload, setShowBrandingUpload] = useState(false);
  const [skipped, setSkipped] = useState(false);

  // Rebuild steps on every render so titles/bodies are always in the current language
  const steps = buildSteps(i18n);
  const currentStep = steps[stepIndex] ?? null;

  // Refs that always point to the latest values - updated synchronously
  // inside every setter so that notifyAction called inside click handlers
  // always reads the correct value, even before React flushes effects.
  const activeRef = React.useRef(active);
  const stepIndexRef = React.useRef(stepIndex);

  const start = useCallback(() => {
    activeRef.current = true;
    stepIndexRef.current = 0;
    setStepIndex(0);
    setActive(true);
    setShowBrandingUpload(false);
  }, []);

  const stop = useCallback(() => {
    const wasActive = activeRef.current;
    const tutorialCompleted = wasActive && stepIndexRef.current >= TOTAL_STEPS - 1;
    if (tutorialCompleted) {
      completeTutorialMutation.mutate();
      trackManagerAction('manager_onboarding_tutorial_completed', { totalSteps: TOTAL_STEPS });
    } else {
      if (wasActive) {
        trackManagerAction('manager_onboarding_skipped', {
          step: stepIndexRef.current + 1,
          stepId: buildSteps(i18n)[stepIndexRef.current]?.id,
          totalSteps: TOTAL_STEPS,
        });
      }
      setSkipped(true);
    }
    activeRef.current = false;
    setActive(false);
    setShowBrandingUpload(false);
  }, [completeTutorialMutation, i18n]);

  const advance = useCallback(() => {
    setStepIndex(prev => {
      const next = prev + 1;
      if (next >= TOTAL_STEPS) {
        activeRef.current = false;
        setActive(false);
        return prev;
      }
      stepIndexRef.current = next;
      return next;
    });
  }, []);

  // Called by page components when a relevant action happens (e.g. form saved).
  const notifyAction = useCallback(
    actionName => {
      if (!activeRef.current) return;
      const stepsSnapshot = buildSteps(i18n);
      const currentStepFromRef = stepsSnapshot[stepIndexRef.current];
      if (!currentStepFromRef) return;
      const triggers = {
        'menu-saved': 'edit-menu-save',
        'category-added': 'categories-add-button',
        'item-saved': 'edit-item-save',
        'branding-submitted': 'branding-upload',
      };
      const triggerStep = triggers[actionName];
      if (triggerStep && currentStepFromRef.id === triggerStep) {
        advance();
      }
    },
    [i18n, advance],
  );

  return (
    <TutorialContext.Provider
      value={{
        active,
        stepIndex,
        steps,
        currentStep,
        showBrandingUpload,
        setShowBrandingUpload,
        start,
        stop,
        advance,
        notifyAction,
        skipped,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial() {
  const ctx = useContext(TutorialContext);
  if (!ctx) throw new Error('useTutorial must be used inside TutorialProvider');
  return ctx;
}
